import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { govPageContainer, GOV_TYPE } from '../ui/PatientUIGovernor';
import { patientApi, type PatientNotificationRow } from '../api';
import { notifyNotificationsChanged } from '../state/notificationsBus';

/**
 * Notifications — the SERVER's list (`GET /notifications`), not a local copy.
 *
 * This page used to render the in-browser store while the header bell counted
 * the server's unread notifications, so the badge showed a number the page
 * could not explain and "mark read" never left the device. Both now read the
 * same rows, and marking read is a real `PATCH /notifications/:id/read`.
 */

/** Filter label → the server's own category enum (notification.model.ts). */
const FILTERS: { label: string; category: string | null }[] = [
  { label: 'All', category: null },
  { label: 'Appointments', category: 'APPOINTMENT' },
  { label: 'Lab', category: 'LAB' },
  { label: 'Medicines', category: 'MEDICINE' },
  { label: 'Records', category: 'RECORDS' },
  { label: 'Orders', category: 'ORDER' },
  { label: 'Consent', category: 'CONSENT' },
  { label: 'Emergency', category: 'EMERGENCY' },
  { label: 'Insurance', category: 'INSURANCE' },
  { label: 'Security', category: 'SECURITY' },
  { label: 'General', category: 'GENERAL' },
];

const CATEGORY_COLOR: Record<string, string> = {
  APPOINTMENT: T.teal,
  LAB: T.info,
  MEDICINE: T.info,
  RECORDS: T.navy,
  ORDER: T.success,
  CONSENT: T.warning,
  EMERGENCY: T.danger,
  INSURANCE: T.teal,
  SECURITY: T.navy,
  GENERAL: T.textSec,
};

/** Where a notification takes the patient when it is opened. */
const CATEGORY_ROUTE: Record<string, string> = {
  APPOINTMENT: '/patient/appointments',
  LAB: '/patient/lab',
  MEDICINE: '/patient/medicines',
  RECORDS: '/patient/health-vault',
  ORDER: '/patient/pharmacy',
  CONSENT: '/patient/consent',
  EMERGENCY: '/patient/emergency',
  INSURANCE: '/patient/insurance',
  SECURITY: '/patient/security',
};

function relativeTime(dateStr: string): string {
  const then = new Date(dateStr).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  const hours = Math.floor(mins / 60);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (hours < 48) return 'Yesterday';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function PatientNotifications() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PatientNotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyAll, setBusyAll] = useState(false);
  const [filter, setFilter] = useState<string>('All');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await patientApi.getNotifications();
      setRows(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setError(e?.body?.message || 'Your notifications could not be loaded. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const activeCategory = FILTERS.find(f => f.label === filter)?.category ?? null;
  const filtered = activeCategory ? rows.filter(n => n.category === activeCategory) : rows;
  const unreadCount = rows.filter(n => !n.read).length;

  /** Mark read on the server, then reflect it here and in the header bell. */
  const markRead = async (id: string) => {
    setRows(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
    try {
      await patientApi.markNotificationRead(id);
      notifyNotificationsChanged();
    } catch {
      // The server did not accept it — put the dot back rather than show a
      // notification as read when it is not.
      setRows(prev => prev.map(n => (n.id === id ? { ...n, read: false } : n)));
    }
  };

  const markAllRead = async () => {
    setBusyAll(true);
    try {
      await patientApi.markAllNotificationsRead();
      notifyNotificationsChanged();
      await load();
    } catch (e: any) {
      setError(e?.body?.message || 'They could not be marked read — nothing was changed.');
    } finally {
      setBusyAll(false);
    }
  };

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...GOV_TYPE.pageTitle }}>Notifications</span>
          {unreadCount > 0 && (
            <span style={{
              fontSize: 9, fontWeight: 700, fontFamily: T.font,
              background: T.teal, color: T.white,
              borderRadius: 10, padding: '2px 7px', minWidth: 18, textAlign: 'center',
            }}>
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            disabled={busyAll}
            style={{
              fontSize: 9, fontWeight: 600, fontFamily: T.font,
              color: T.teal, background: 'none', border: 'none',
              cursor: busyAll ? 'default' : 'pointer', padding: 0, opacity: busyAll ? 0.5 : 1,
            }}
          >
            {busyAll ? 'Marking…' : 'Mark All Read'}
          </button>
        )}
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
        {FILTERS.map(f => (
          <button
            key={f.label}
            onClick={() => setFilter(f.label)}
            style={{
              fontSize: 9, fontWeight: 600, fontFamily: T.font,
              padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
              background: filter === f.label ? T.teal : `${T.teal}10`,
              color: filter === f.label ? '#FFF' : T.teal,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
          padding: 40, boxShadow: T.shadow, textAlign: 'center',
          fontSize: 10, color: T.textSec, fontFamily: T.font,
        }}>
          Loading your notifications…
        </div>
      ) : error ? (
        <div style={{
          background: T.white, border: `1px solid ${T.danger}40`, borderRadius: 16,
          padding: 24, boxShadow: T.shadow, textAlign: 'center', fontFamily: T.font,
        }}>
          <div style={{ fontSize: 10.5, color: T.danger, fontWeight: 600 }}>{error}</div>
          <button
            onClick={() => void load()}
            style={{
              marginTop: 10, fontSize: 10, fontWeight: 700, fontFamily: T.font,
              color: T.white, background: T.teal, border: 'none',
              borderRadius: 8, padding: '7px 16px', cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
          padding: 40, boxShadow: T.shadow, textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font }}>
            {rows.length === 0 ? 'No notifications yet' : `Nothing under ${filter}`}
          </div>
          <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, marginTop: 4, lineHeight: 1.5 }}>
            {rows.length === 0
              ? 'Notifications from your bookings, orders, and actions will appear here.'
              : 'Choose another filter to see the rest.'}
          </div>
        </div>
      ) : (
        filtered.map(n => {
          const dotColor = CATEGORY_COLOR[n.category] || T.textSec;
          const route = CATEGORY_ROUTE[n.category];
          return (
            <button
              key={n.id}
              onClick={() => {
                if (!n.read) void markRead(n.id);
                if (route) navigate(route);
              }}
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', textAlign: 'left',
                width: '100%',
                background: n.read ? T.white : T.ice,
                border: `1px solid ${T.border}`,
                borderRadius: 14,
                padding: 12,
                cursor: 'pointer',
                boxShadow: T.shadow,
                position: 'relative',
                fontFamily: T.font,
              }}
            >
              {/* Unread indicator */}
              {!n.read && (
                <div style={{
                  position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
                  width: 5, height: 5, borderRadius: '50%', background: T.teal,
                }} />
              )}

              {/* Category dot */}
              <div style={{
                width: 32, height: 32, borderRadius: 10,
                background: `${dotColor}12`,
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                <div style={{ width: 8, height: 8, borderRadius: 4, background: dotColor }} />
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 2 }}>
                  {n.title}
                </div>
                {n.body && (
                  <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, lineHeight: 1.4 }}>
                    {n.body}
                  </div>
                )}
                <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, marginTop: 4 }}>
                  {relativeTime(n.createdAt)}
                </div>
              </div>
            </button>
          );
        })
      )}
    </div>
  );
}
